import { useCallback, useEffect, useMemo, useState } from 'react';
import { CheckCircle2, Copy, RefreshCw, ShieldX } from 'lucide-react';
import AppLayout from '@/components/Layouts/AppLayout';
import { useAlert } from '@/hooks/useAlert';
import { useAuthorization } from '@/auth/hooks/useAuthorization';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import Paginator from '@/components/Paginator';
import { temporaryLinksService, TemporaryLinkItem, TemporaryLinkStatus } from '@/services/temporaryLinksService';

const STATUS_OPTIONS: Array<{ value: ''; label: string } | { value: TemporaryLinkStatus; label: string }> = [
  { value: '', label: 'Todos los estados' },
  { value: 'active', label: 'Activo' },
  { value: 'revoked', label: 'Revocado' },
  { value: 'expired', label: 'Expirado' },
  { value: 'max_downloads_reached', label: 'Límite alcanzado' },
];

const statusBadgeVariant = (
  status: TemporaryLinkStatus
): 'default' | 'secondary' | 'destructive' | 'outline' => {
  if (status === 'active') return 'default';
  if (status === 'revoked') return 'destructive';
  if (status === 'expired') return 'secondary';
  return 'outline';
};

const getPageFromUrl = (url: string | null): number | null => {
  if (!url) return null;
  try {
    const parsed = new URL(url);
    const page = parsed.searchParams.get('page');
    return page ? parseInt(page, 10) : null;
  } catch {
    return null;
  }
};

const formatTramiteReference = (item: TemporaryLinkItem): string => {
  const rawId = item.tramite?.id ?? item.tramiteId;
  if (rawId === null || rawId === undefined || rawId === '') {
    return '-';
  }

  if (typeof rawId === 'number') {
    return `#${rawId}`;
  }

  const trimmed = String(rawId).trim();
  if (/^\d+$/.test(trimmed)) {
    return `#${trimmed}`;
  }

  return trimmed;
};

const TemporaryLinksPage = () => {
  const { errorAlert, successAlert, questionAlert } = useAlert();
  const { canViewTemporaryLinks, canDeleteTemporaryLinks } = useAuthorization();

  const [status, setStatus] = useState<'' | TemporaryLinkStatus>('');
  const [tramiteId, setTramiteId] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const [links, setLinks] = useState<TemporaryLinkItem[]>([]);
  const [paginationLinks, setPaginationLinks] = useState<Array<{ label: string; url: string | null; active: boolean; onClick: () => void }>>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [revokingId, setRevokingId] = useState<string | null>(null);
  const [restoringId, setRestoringId] = useState<string | null>(null);

  const fetchLinks = useCallback(async (
    page: number = 1,
    overrides?: {
      status?: '' | TemporaryLinkStatus;
      tramiteId?: string;
      dateFrom?: string;
      dateTo?: string;
    }
  ) => {
    setLoading(true);
    try {
      const nextStatus = overrides?.status ?? status;
      const nextTramiteId = overrides?.tramiteId ?? tramiteId;
      const nextDateFrom = overrides?.dateFrom ?? dateFrom;
      const nextDateTo = overrides?.dateTo ?? dateTo;

      const response = await temporaryLinksService.listTemporaryLinks({
        status: nextStatus || undefined,
        tramite_id: nextTramiteId.trim() || undefined,
        date_from: nextDateFrom || undefined,
        date_to: nextDateTo || undefined,
        page,
      });

      setLinks(response.data);
      setCurrentPage(response.current_page);
      setTotal(response.total);
      setPaginationLinks(
        (response.links || []).map((link) => ({
          ...link,
          onClick: () => {
            const targetPage = getPageFromUrl(link.url);
            if (!targetPage || targetPage === response.current_page) return;
            void fetchLinks(targetPage);
          },
        })),
      );
    } catch (error: any) {
      errorAlert({ title: 'Error al cargar enlaces temporales', exception: error });
    } finally {
      setLoading(false);
    }
  }, [status, tramiteId, dateFrom, dateTo, errorAlert]);

  useEffect(() => {
    if (canViewTemporaryLinks) {
      void fetchLinks(1);
    }
    // fetchLinks depende de callbacks de alerta que no son estables y pueden causar loops de refetch.
    // Solo queremos carga inicial al habilitar acceso a la pantalla.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canViewTemporaryLinks]);

  const handleCopyUrl = async (url: string) => {
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = url;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      successAlert({ message: 'URL copiada al portapapeles' });
    } catch {
      errorAlert({ message: 'No se pudo copiar la URL' });
    }
  };

  const handleRevoke = async (link: TemporaryLinkItem) => {
    const confirmation = await questionAlert({
      title: 'Revocar enlace temporal',
      text: 'Esta acción bloqueará descargas futuras para este enlace. ¿Confirmas?',
      confirmButtonText: 'Revocar',
      cancelButtonText: 'Cancelar',
    });

    if (!confirmation?.isConfirmed) return;

    setRevokingId(link.id);
    try {
      await temporaryLinksService.revokeTemporaryLink(link.id);
      successAlert({ message: 'Enlace temporal revocado exitosamente' });
      await fetchLinks(currentPage);
    } catch (error: any) {
      errorAlert({ title: 'Error al revocar enlace', exception: error });
    } finally {
      setRevokingId(null);
    }
  };

  const handleRestore = async (link: TemporaryLinkItem) => {
    const confirmation = await questionAlert({
      title: 'Reactivar enlace temporal',
      text: 'Esta acción volverá a habilitar descargas para este enlace si no está expirado o vencido por límite. ¿Confirmas?',
      confirmButtonText: 'Activar',
      cancelButtonText: 'Cancelar',
    });

    if (!confirmation?.isConfirmed) return;

    setRestoringId(link.id);
    try {
      await temporaryLinksService.restoreTemporaryLink(link.id);
      successAlert({ message: 'Enlace temporal reactivado exitosamente' });
      await fetchLinks(currentPage);
    } catch (error: any) {
      errorAlert({ title: 'Error al reactivar enlace', exception: error });
    } finally {
      setRestoringId(null);
    }
  };

  const canRenderTable = useMemo(() => canViewTemporaryLinks, [canViewTemporaryLinks]);

  return (
    <AppLayout title="Enlaces temporales">
      <div className="bg-white p-4 mx-auto shadow-md rounded-md space-y-4">
        {!canRenderTable ? (
          <p className="text-sm text-gray-600">No tienes permisos para ver enlaces temporales.</p>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-2">
              <select
                className="h-10 rounded-md border px-3 text-sm"
                value={status}
                onChange={(e) => setStatus((e.target.value as TemporaryLinkStatus) || '')}
              >
                {STATUS_OPTIONS.map((option) => (
                  <option key={option.label} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>

              <Input
                type="text"
                placeholder="ID Trámite (ej: 782 u OT-00082)"
                value={tramiteId}
                onChange={(e) => setTramiteId(e.target.value)}
              />

              <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
              <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />

            </div>

            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" onClick={() => void fetchLinks(1)} disabled={loading}>
                <RefreshCw className="w-4 h-4 mr-1" />
                Actualizar
              </Button>
            </div>

            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Trámite</TableHead>
                  <TableHead>Archivo</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead>Expira</TableHead>
                  <TableHead>Descargas</TableHead>
                  <TableHead>Creador</TableHead>
                  <TableHead>Creado</TableHead>
                  <TableHead>URL</TableHead>
                  <TableHead>Acción</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {!loading && links.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center text-sm text-gray-500">
                      No hay enlaces temporales para los filtros aplicados.
                    </TableCell>
                  </TableRow>
                )}

                {links.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>
                      {formatTramiteReference(item)}
                    </TableCell>
                    <TableCell>
                      <span className="block max-w-[220px] truncate" title={item.fileName || ''}>
                        {item.fileName || '-'}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Badge variant={statusBadgeVariant(item.status)}>{item.statusLabel}</Badge>
                    </TableCell>
                    <TableCell>{item.expiresAt ? new Date(item.expiresAt).toLocaleString() : 'Sin expiración'}</TableCell>
                    <TableCell>
                      {item.downloadCount}
                      {item.maxDownloads ? ` / ${item.maxDownloads}` : ''}
                    </TableCell>
                    <TableCell>{item.createdBy?.name || 'Sistema'}</TableCell>
                    <TableCell>{new Date(item.createdAt).toLocaleString()}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2 max-w-[320px]">
                        <span className="truncate text-xs" title={item.url}>{item.url}</span>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => void handleCopyUrl(item.url)}
                          title="Copiar URL"
                        >
                          <Copy className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                    <TableCell>
                      {canDeleteTemporaryLinks ? (
                        item.status === 'revoked' ? (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => void handleRestore(item)}
                            disabled={restoringId === item.id}
                          >
                            <CheckCircle2 className="w-4 h-4 mr-1" />
                            Activar
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => void handleRevoke(item)}
                            disabled={revokingId === item.id}
                          >
                            <ShieldX className="w-4 h-4 mr-1" />
                            Revocar
                          </Button>
                        )
                      ) : (
                        <span className="text-xs text-gray-400">Sin permiso</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <div className="text-sm text-gray-600 text-center">
              Mostrando {links.length} de {total} enlaces
            </div>

            <Paginator links={paginationLinks} visible={!loading} />
          </>
        )}
      </div>
    </AppLayout>
  );
};

export default TemporaryLinksPage;
